import { useState, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getAuthToken } from "@/lib/queryClient";
import { stripFormDataImages } from "@/lib/strip-image-metadata";
import { Upload, Trash2, ImageIcon, User, Palette, Tag, Loader2, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BrandAsset {
  id: string;
  name: string;
  description: string;
  fileUrl: string;
  fileType: string;
  mimeType: string;
  fileSizeBytes: number | null;
  createdAt: string;
}

const ASSET_TYPE_OPTIONS = [
  { value: "logo", label: "Logo", icon: <Palette size={12} /> },
  { value: "photo", label: "Zdjęcie", icon: <ImageIcon size={12} /> },
  { value: "person", label: "Osoba", icon: <User size={12} /> },
  { value: "scene", label: "Scena", icon: <ImageIcon size={12} /> },
  { value: "graphic", label: "Grafika", icon: <Palette size={12} /> },
  { value: "other", label: "Inne", icon: <Tag size={12} /> },
] as const;

const ASSET_TYPE_LABELS: Record<string, string> = {
  logo: "Logo",
  photo: "Zdjęcie",
  person: "Osoba",
  scene: "Scena",
  graphic: "Grafika",
  other: "Inne",
};

type AssetTypeFilter = string | "all";

export default function CarouselBrandAssets() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showUpload, setShowUpload] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadType, setUploadType] = useState("photo");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<AssetTypeFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: assets = [], isLoading } = useQuery<BrandAsset[]>({
    queryKey: ["/api/carousel-creator/brand-assets"],
    queryFn: () => apiRequest("GET", "/api/carousel-creator/brand-assets"),
  });

  const uploadMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const token = getAuthToken();
      const resp = await fetch("/api/carousel-creator/brand-assets", {
        method: "POST",
        body: await stripFormDataImages(formData),
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: "Błąd uploadu" }));
        throw new Error(err.message);
      }
      return resp.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carousel-creator/brand-assets"] });
      resetUploadForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/carousel-creator/brand-assets/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carousel-creator/brand-assets"] });
    },
  });

  const resetUploadForm = useCallback(() => {
    setShowUpload(false);
    setUploadName("");
    setUploadDescription("");
    setUploadType("photo");
    setSelectedFile(null);
    setPreviewUrl(null);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      if (!uploadName) {
        setUploadName(file.name.replace(/\.[^.]+$/, ""));
      }
    },
    [uploadName]
  );

  const handleUpload = useCallback(() => {
    if (!selectedFile || !uploadName.trim() || !uploadDescription.trim()) return;

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("name", uploadName.trim());
    formData.append("description", uploadDescription.trim());
    formData.append("fileType", uploadType);

    uploadMutation.mutate(formData);
  }, [selectedFile, uploadName, uploadDescription, uploadType, uploadMutation]);

  const filteredAssets = assets.filter((asset) => {
    if (filterType !== "all" && asset.fileType !== filterType) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return asset.name.toLowerCase().includes(q) || asset.description.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] shrink-0">
        <span className="text-xs font-semibold text-white/60">Assety firmowe</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowUpload(!showUpload)}
          className="h-7 px-2 text-xs gap-1"
        >
          {showUpload ? <X size={12} /> : <Upload size={12} />}
          {showUpload ? "Anuluj" : "Dodaj"}
        </Button>
      </div>

      {/* Upload form */}
      {showUpload && (
        <div className="px-4 py-3 border-b border-white/[0.06] space-y-3 bg-white/[0.02]">
          {/* File picker */}
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-white/10 rounded-lg p-4 text-center cursor-pointer hover:border-white/25 transition-colors"
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="w-20 h-20 object-cover rounded-md mx-auto mb-2"
              />
            ) : (
              <ImageIcon size={24} className="text-white/20 mx-auto mb-2" />
            )}
            <p className="text-[11px] text-white/40">
              {selectedFile ? selectedFile.name : "Kliknij, aby wybrać plik (JPG, PNG, WEBP, SVG)"}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/svg+xml"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Name */}
          <input
            type="text"
            value={uploadName}
            onChange={(e) => setUploadName(e.target.value)}
            placeholder="Nazwa (np. Zdjęcie profilowe Pawła)"
            maxLength={200}
            className="w-full h-8 px-3 text-xs bg-white/5 border border-white/10 rounded-md text-white/80 placeholder:text-white/30"
          />

          {/* Description */}
          <textarea
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.target.value)}
            placeholder="Opis (np. Zdjęcie profilowe CEO firmy, tło biurowe, ubrany na elegancko)"
            maxLength={2000}
            rows={2}
            className="w-full px-3 py-2 text-xs bg-white/5 border border-white/10 rounded-md text-white/80 placeholder:text-white/30 resize-none"
          />

          {/* Type selector */}
          <div className="flex flex-wrap gap-1.5">
            {ASSET_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setUploadType(opt.value)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                  uploadType === opt.value
                    ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                    : "bg-white/5 text-white/40 border border-white/10 hover:text-white/60"
                }`}
              >
                {opt.icon}
                {opt.label}
              </button>
            ))}
          </div>

          {/* Submit */}
          <Button
            onClick={handleUpload}
            disabled={
              !selectedFile ||
              !uploadName.trim() ||
              !uploadDescription.trim() ||
              uploadMutation.isPending
            }
            className="w-full h-8 text-xs"
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 size={12} className="animate-spin mr-1" /> Przesyłanie...
              </>
            ) : (
              <>
                <Upload size={12} className="mr-1" /> Prześlij asset
              </>
            )}
          </Button>

          {uploadMutation.isError && (
            <p className="text-[11px] text-red-400">{(uploadMutation.error as Error).message}</p>
          )}
        </div>
      )}

      {/* Filter bar */}
      <div className="px-4 py-2 border-b border-white/[0.04] space-y-2">
        {/* Search */}
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/20" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Szukaj..."
            className="w-full h-7 pl-7 pr-3 text-[11px] bg-white/5 border border-white/10 rounded-md text-white/80 placeholder:text-white/30"
          />
        </div>
        {/* Type filter */}
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setFilterType("all")}
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
              filterType === "all" ? "bg-white/10 text-white" : "text-white/30 hover:text-white/60"
            }`}
          >
            Wszystkie
          </button>
          {ASSET_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilterType(opt.value)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                filterType === opt.value
                  ? "bg-white/10 text-white"
                  : "text-white/30 hover:text-white/60"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Assets grid */}
      <div className="flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={20} className="animate-spin text-white/20" />
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="w-8 h-8 text-white/10 mx-auto mb-2" />
            <p className="text-white/30 text-xs">
              {assets.length === 0
                ? "Brak assetów. Dodaj zdjęcia, logo i grafiki firmowe."
                : "Brak wyników dla wybranych filtrów."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                className="group relative bg-white/[0.03] rounded-lg border border-white/[0.06] overflow-hidden hover:border-white/15 transition-all"
              >
                {/* Image */}
                <div className="aspect-square overflow-hidden">
                  <img
                    src={asset.fileUrl}
                    alt={asset.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                {/* Info */}
                <div className="p-2">
                  <p className="text-[11px] font-medium text-white/80 truncate">{asset.name}</p>
                  <p className="text-[10px] text-white/40 truncate mt-0.5">{asset.description}</p>
                  <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-white/5 text-white/40">
                    {ASSET_TYPE_LABELS[asset.fileType] ?? asset.fileType}
                  </span>
                </div>
                {/* Delete button */}
                <button
                  onClick={() => deleteMutation.mutate(asset.id)}
                  disabled={deleteMutation.isPending}
                  className="absolute top-1.5 right-1.5 p-1 rounded bg-black/60 text-white/40 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
